import React, { useState, useMemo, useEffect } from 'react';
import {useTranslation} from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from 'react-router-dom';
import {Badge} from "react-bootstrap";
import classNames from 'class-names';
import {Plus, Minus, Clock, Users, MapPin, UserCheck} from "react-feather";

import Service from "../Service/Service";

import Styles from './ServiceOptions.module.scss';

import { ROUTER_URL } from '../../const/ROUTER_URL';

const ServiceOptions = ({service, group, event, bundle, type = 'booking', expanded, onExpandChanged, to}) => {
    const { shopId } = useParams();

    const [isExpanded, setExpanded] = useState(false);
    const [isHovered, setHovered] = useState(false);
    const tintColor = useSelector(state => state.companies.tintColor);

    const {t} = useTranslation('common');

    const { formatedService, subServices } = useMemo(() => {
        switch (type) {
            case 'booking':
                return {
                    formatedService: {
                        id: service?.id,
                        name: service?.name,
                        subName: service?.subName,
                        price: service?.price,
                        length: service?.length,
                        isHidePrice: service?.hide_price,
                    },
                    subServices: service?.sub_options.map(((subOption) => ({
                        ...subOption,
                        isHidePrice: service?.hide_price,
                    }))) ?? [],
                }
            case 'group':
                return {
                    formatedService: {
                        id: `${group.id}_${group.serviceId}`,
                        name: group?.groupName,
                        subName: group?.serviceName,
                        price: group?.cost,
                        length: group?.length,
                        showSessionLeft: group?.show_sessions_left,
                        available: group?.available,
                        total: group?.total,
                        employeeName: group?.employee_name,
                        street: group?.street,
                        city: group?.city,
                        zip: group?.zip,
                        closed: group?.isClose,
                        cancelled: group?.isCancelled,
                        booked: group?.isBooking,
                        isHidePrice: group?.hide_price,
                    },
                    subServices: group?.serviceSubOptions.map((subOption) => ({
                        id: `${subOption.id}_${subOption.serviceId}`,
                        name: subOption.serviceName,
                        price: subOption.cost,
                        length: subOption.length,
                        booked: subOption.isBooking,
                        closed: subOption.isClose,
                        cancelled: subOption.isCancelled,
                        isHidePrice: group.hide_price,
                    })) ?? [],
                }
            case 'event':
                return {
                    formatedService: {
                        id: event?.serviceId,
                        name: event?.groupName,
                        subName: event?.serviceName,
                        price: event?.cost,
                        length: event?.length,
                        showSessionLeft: event?.show_sessions_left,
                        available: event?.available,
                        total: event?.total,
                        employeeName: event?.employee_name,
                        street: event?.street,
                        city: event?.city,
                        zip: event?.zip,
                        closed: event?.isClose,
                        cancelled: event?.isCancelled,
                        booked: event?.isBooking,
                        isHidePrice: event?.hide_price,
                    },
                    subServices: event?.serviceSubOptions.map((subOption) => ({
                        id: subOption.serviceId,
                        name: subOption.serviceName,
                        price: subOption.cost,
                        length: subOption.length,
                        booked: subOption.isBooking,
                        closed: subOption.isClose,
                        cancelled: subOption.isCancelled,
                        isHidePrice: event.hide_price,
                    })) ?? [],
                }
            default:
                return {
                    formatedService: {
                        id: bundle?.id,
                        name: bundle?.name,
                        subName: bundle?.description,
                        price: bundle?.price,
                        length: bundle?.length,
                        quantity: bundle?.max_usages,
                        bought: Boolean(bundle?.reservation_id),
                        isHidePrice: bundle?.hide_price,
                    },
                    subServices: [],
                }
        }
    }, [type, service, group, event, bundle])

    useEffect(() => {
        setExpanded(expanded)
    }, [expanded]);

    if (subServices.length === 0) {
        return (
            <Service
                service={formatedService}
                type={type}
                to={to}
            />
        )
    }

    return (
        <div className={Styles.container}>
            <button
                data-testid={`data-test-service-button-${formatedService.name}`}
                disabled={formatedService.closed}
                className={classNames(
                    Styles.button,
                    isExpanded ? Styles.expanded : ''
                )}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => {
                    onExpandChanged(!isExpanded);
                    setExpanded(!isExpanded);
                }}
            >
                <div className={Styles.detailsContainer}>
                    <span className={Styles.name} data-testid='data-test-name-container'>
                        <span data-testid='data-test-name'>{formatedService.name}</span>
                        {formatedService.closed && (
                            <Badge
                                variant={'danger'}
                                className={'ml-3 text-uppercase'}
                                data-testid={'data-test-service-full'}
                            >
                                {t('homeScreen.events.full')}
                            </Badge>
                        )}
                        {subServices.some(e => e.booked) && (
                            <Badge
                                variant={'success'}
                                className={'ml-3 text-uppercase'}
                                data-testid={'data-test-service-booked'}
                            >
                                {t('homeScreen.events.booked')}
                            </Badge>
                        )}
                        {formatedService.cancelled && (
                            <Badge
                                variant={'danger'}
                                className={'ml-3 text-uppercase'}
                                data-testid={'data-test-service-cancelled'}
                            >
                                {t('homeScreen.events.cancelled')}
                            </Badge>
                        )}
                    </span>
                    {(type === 'group' || type ==='event') && (
                        <>
                            <span className={Styles.description} data-testid={'data-test-subtitle'}>
                                {formatedService.subName}
                            </span>

                            <div className={Styles.details}>
                                <div className={Styles.detail}>
                                    <div>
                                        <Clock
                                            size={18}
                                            color={isHovered ? tintColor : undefined}
                                        />
                                    </div>

                                    <span className={Styles.value} data-testid={'data-test-length'}>
                                        {formatedService.length}
                                    </span>
                                </div>

                                {Boolean(formatedService.available && formatedService.total && formatedService.showSessionLeft)
                                    && (
                                        <div className={Styles.detail}>
                                            <div>
                                                <Users
                                                    size={18}
                                                    color={isHovered ? tintColor : undefined}
                                                />
                                            </div>
                                            <span className={Styles.value} data-testid={'data-test-available'}>
                                                {`${formatedService.total-formatedService.available}/${formatedService.total}`}
                                            </span>
                                        </div>
                                    )
                                }

                                {Boolean(formatedService.employeeName) && (
                                    <div className={Styles.teacher}>
                                        <div>
                                            <UserCheck
                                                size={18}
                                                color={isHovered ? tintColor : undefined}
                                            />
                                        </div>
                                        <span className={Styles.value} data-testid={'data-test-employee'}>
                                            {formatedService.employeeName}
                                        </span>
                                    </div>
                                )}

                                <div className={Styles.address}>
                                    <div>
                                        <MapPin
                                            size={18}
                                            color={isHovered ? tintColor : undefined}
                                        />
                                    </div>
                                    <span className={Styles.value} data-testid={'data-test-address'}>
                                        {`${formatedService.street}, ${formatedService.zip}, ${formatedService.city}`}
                                    </span>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="h-100 d-flex align-items-center">
                    {isExpanded
                        ? <Minus className={Styles.icon}/>
                        : <Plus className={Styles.icon}/>
                    }
                </div>
            </button>

            <div className={classNames(Styles.options, isExpanded ? Styles.expanded : '')}>
                {subServices.map((service) => (
                    <Service
                        key={`service-${service.id}`}
                        service={service}
                        type={type}
                        to={type === 'event'
                            ? ROUTER_URL.SHOPS_ID_EVENTS_ID({ shopId , eventId: service.id })
                            : to
                        }
                        indent={true}
                    />
                ))}
            </div>
        </div>
    )
};

export default ServiceOptions
